import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import CopyButton from '../../components/ui/CopyButton.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDate, fmtDateTime } from '../../utils/format.js';
import DiscoverySections from './DiscoverySections.jsx';
import { REQUEST_STATUS } from './domainMeta.js';
import DomainStatus from './DomainStatus.jsx';
import { VerifiedSeal } from '../../components/ui/VerifiedSeal.jsx';
import { Link } from 'react-router-dom';
import { checkEmail } from '../../utils/validate.js';

/** Why a proof didn't verify the domain: someone else holds it, or the domain above it. */
export const conflictText = (dom) => (dom.parent_held_by_another_company
  ? `${dom.domain} sits under ${dom.parent_held_by_another_company}, which another company has verified, so only they can verify it. If it’s yours, contact support@summitshield.io.`
  : `Another company has already verified ${dom.domain}. If it’s yours, contact support@summitshield.io.`);


function Check({ done, children }) {
  return (
    <li className={`check-item${done ? ' is-done' : ''}`}>
      <span className="check-mark" aria-hidden="true">{done ? '✓' : ''}</span>
      <span>{children}<span className="sr-only">{done ? ' — done' : ' — not yet'}</span></span>
    </li>
  );
}

/** A DNS check result in plain words. */
function dnsResult(r) {
  const s = String(r || '');
  if (s === 'found') return 'Found — the record is in place.';
  if (/^not found|^no TXT/i.test(s)) return 'We looked, but the record isn’t there yet.';
  if (/^lookup failed/i.test(s)) return 'We couldn’t reach your domain’s settings just then. Try again in a few minutes.';
  return s ? `${s}.` : '';
}

// The three settings that stop people faking email from a domain, in plain words
function securityRows(sec, domain) {
  const policy = sec.dmarc;
  const enforcing = policy === 'reject' || policy === 'quarantine';
  const pctOk = enforcing && (sec.dmarc_pct == null || Number(sec.dmarc_pct) === 100);
  return [
    {
      key: 'dmarc', done: policy === 'reject' && pctOk,
      title: 'Email providers refuse fake email in your name',
      tech: 'DMARC',
      now: policy === 'reject' ? (pctOk ? 'On: fake email is refused.' : `Partly on: it only covers ${sec.dmarc_pct}% of your email.`)
        : policy === 'quarantine' ? 'Partly on: fake email goes to spam instead of being refused.'
          : policy === 'none' ? 'Watching only: fake email still gets through.' : 'Off: nothing tells providers what to do with fake email.',
      why: 'Without it, anyone can send email that says it’s from you, and most inboxes will show it.',
      how: policy === 'reject' && pctOk ? null : {
        text: `Ask whoever runs ${domain}’s settings to add (or change) a TXT record named _dmarc with this value. Once the two settings below are on, this is safe to switch on.`,
        record: { type: 'TXT', name: '_dmarc', value: 'v=DMARC1; p=reject; adkim=r; aspf=r' },
      },
    },
    {
      key: 'spf', done: ['-all', '~all'].includes(sec.spf_all),
      title: 'Only the services you use may send as you',
      tech: 'SPF',
      now: ['-all', '~all'].includes(sec.spf_all) ? 'On.' : sec.spf_all ? 'Partly on: other servers aren’t blocked.' : 'Off: there’s no list of who may send as you.',
      why: 'It lists the services allowed to send your email (your mailbox, your newsletter tool) and tells everyone else they aren’t.',
      how: ['-all', '~all'].includes(sec.spf_all) ? null : { text: 'Your email provider and newsletter tool each publish the line to add. Ask whoever runs your domain to combine them into one SPF record ending “-all”.' },
    },
    {
      key: 'dkim', done: !!sec.dkim,
      title: 'Your email is digitally signed',
      tech: 'DKIM',
      now: sec.dkim ? 'On: we found a signature key.' : 'Not found. We check the common names, so it may already be on.',
      why: 'A signature proves an email came from your systems and wasn’t changed on the way.',
      how: sec.dkim ? null : { text: 'Turn on “DKIM signing” in your email provider’s admin settings (Microsoft 365, Google Workspace and most newsletter tools have it). They’ll give you the records to add.' },
    },
  ];
}

function EmailSecurity({ domain, security, help, provider }) {
  if (!security) return null;
  const rows = securityRows(security, domain);
  const onCount = rows.filter((r) => r.done).length;
  return (
    <Section title={`Stop people faking email from ${domain}`}
      description="Three settings on your domain tell other email systems how to spot fake email in your name. They’re free, and whoever runs your domain or IT can switch them on. Each one also raises your protection score.">
      <p className="small"><strong>{onCount} of 3 on.</strong>{provider && help && <> Your domain is run by {provider}: <a href={help} target="_blank" rel="noreferrer">their guide to adding records</a>.</>}</p>
      <ul className="security-list">
        {rows.map((r) => (
          <li key={r.key} className={r.done ? 'is-done' : ''}>
            <span className="security-mark" aria-hidden="true">{r.done ? '✓' : '!'}</span>
            <div>
              <h3>{r.title} <span className="muted small">({r.tech})</span></h3>
              <p className="small"><strong>{r.now}</strong> {r.why}</p>
              {r.how && <p className="small muted">{r.how.text}</p>}
              {r.how?.record && (
                <dl className="dns-record">
                  <dt>Type</dt><dd className="mono">{r.how.record.type}</dd>
                  <dt>Name / host</dt><dd className="mono">{r.how.record.name}</dd>
                  <dt>Value</dt><dd className="dns-value"><span className="mono">{r.how.record.value}</span> <CopyButton text={r.how.record.value} /></dd>
                </dl>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** Nominate a colleague's address on the domain to approve by email. */
export function AskColleagueModal({ base, domain, onClose, onSent }) {
  const [choice, setChoice] = useState(domain.suggested_addresses[0]);
  const [custom, setCustom] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const email = choice === 'other' ? custom.trim().toLowerCase() : choice;
  const pick = (c) => { setChoice(c); setErrors({}); };

  // The server's rules, checked first: an address on this domain, and not your own
  const problem = () => {
    if (choice !== 'other') return '';
    return checkEmail(custom, { label: 'their email address' })
      || (!email.endsWith(`@${domain.domain}`) ? `Use an address ending @${domain.domain}.` : '')
      || (email === String(user?.email || '').toLowerCase() ? 'It has to be someone other than you — that’s what makes it a second proof.' : '');
  };

  const send = async () => {
    const p = problem();
    if (p) { setErrors({ email: p }); return; }
    setBusy(true); setError(''); setErrors({});
    try {
      const res = await api(`${base}/approvals`, { method: 'POST', body: { email } });
      onSent(res);
    } catch (err) {
      if (err.data?.fields) setErrors(err.data.fields); else setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Ask a colleague to approve" onClose={onClose} onSubmit={send} busy={busy}
      description={`We’ll email them a link to confirm ${domain.domain} belongs to your company. It works for 24 hours. It has to be someone other than you.`}
      footer={<ModalActions submitLabel="Send the email" busyLabel="Sending…" busy={busy} onCancel={onClose} disabled={!email} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <fieldset className="fieldset">
        <legend>Who should approve?</legend>
        <p className="muted small">These are addresses most companies have. Pick one that reaches someone at {domain.domain} who can say the domain is yours.</p>
        {domain.suggested_addresses.map((a) => (
          <label key={a} className={`radio-card${choice === a ? ' is-selected' : ''}`}>
            <input type="radio" name="approver" checked={choice === a} onChange={() => pick(a)} />
            <span><strong className="mono">{a}</strong></span>
          </label>
        ))}
        <label className={`radio-card${choice === 'other' ? ' is-selected' : ''}`}>
          <input type="radio" name="approver" checked={choice === 'other'} onChange={() => pick('other')} />
          <span><strong>Someone else</strong><span className="muted small">A colleague&rsquo;s own address at {domain.domain}</span></span>
        </label>
      </fieldset>
      {choice === 'other' && (
        <Field label="Their email address" type="email" value={custom} onChange={(e) => { setCustom(e.target.value); setErrors({}); }}
          onBlur={() => custom.trim() && setErrors({ email: problem() || undefined })}
          placeholder={`name@${domain.domain}`} error={errors.email} autoFocus autoComplete="off" autoCapitalize="none" />
      )}
      {choice !== 'other' && errors.email && <p className="field-error">{errors.email}</p>}
    </Modal>
  );
}

export default function DomainVerify() {
  const { domainId } = useParams();
  const { organisation: current, user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [asking, setAsking] = useState(false);
  const [checking, setChecking] = useState(null);   // 'dns' | 'site' | null

  const base = current ? `/portal/organisations/${current.id}/domains/${domainId}` : '';
  const load = useCallback(async () => {
    if (!current) return;
    try { setData(await api(base)); setError(''); } catch (err) { setError(err.status === 404 ? 'This domain isn’t one of yours.' : err.message); }
  }, [base, current]);
  useEffect(() => { load(); }, [load]);

  const back = { to: '/company', label: 'Company' };
  if (error) return <div className="page"><PageHeader back={back} title="Domain" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;

  const { domain: d, organisation: org, discovery } = data;
  const verified = d.status === 'verified';
  const teamDone = d.proofs.team_member.length > 0;
  const approvalDone = d.proofs.approval.length > 0;

  const checkSite = async () => {
    setChecking('site'); setNotice(null);
    try {
      const res = await api(`${base}/check-website`, { method: 'POST' });
      setData({ ...data, domain: { ...res.domain, website_check_result: res.result } });
      if (res.conflict) setNotice({ tone: 'danger', text: conflictText(res.domain || d) });
      else if (res.found) { setNotice({ tone: 'success', text: `Found the code on your site. ${d.domain} is verified.` }); await refresh(); }
      else setNotice({ tone: 'warn', text: `We didn’t find our code on ${d.domain} yet (${res.result}). If you’ve just added it, publish your site and check again.` });
    } catch (err) { setNotice({ tone: 'danger', text: err.message }); }
    setChecking(null);
  };

  const checkDns = async () => {
    setChecking('dns'); setNotice(null);
    try {
      const res = await api(`${base}/check-dns`, { method: 'POST' });
      setData({ ...data, domain: res.domain });
      if (res.conflict) setNotice({ tone: 'danger', text: conflictText(res.domain || d) });
      else if (res.found) { setNotice({ tone: 'success', text: `Found it. ${d.domain} is verified.` }); await refresh(); }
      else setNotice({ tone: 'warn', text: `${dnsResult(res.result)} Changes to a domain’s settings can take a few minutes to show, sometimes up to an hour.` });
    } catch (err) {
      setNotice({ tone: 'danger', text: err.message });
    }
    setChecking(null);
  };

  return (
    <div className="page">
      <PageHeader back={back} title={<>{d.domain}{verified && <VerifiedSeal size={26} title="Verified by SummitShield" />}</>}
        meta={<><DomainStatus status={d.status} /><span className="muted">Domain of {org.name}</span></>}
        subtitle={verified ? undefined : 'Prove it’s yours, so we can tell people a message from this domain really comes from you — and nobody else can claim it. Any one of the three ways below is enough.'} />

      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
      {verified && (
        <Alert tone="success" title={`${d.domain} is verified`}>
          {d.verified_via === 'dns_txt' && 'By its DNS record. We check it regularly; keep the record in place.'}
          {d.verified_via === 'mailbox_link' && `By a team member’s address and a colleague’s approval. This lasts until ${fmtDate(d.expires_at)}; we’ll remind you to renew it.`}
          {d.verified_via === 'admin_attested' && 'By the SummitShield team.'}
        </Alert>
      )}
      {d.parent_held_by_another_company && !verified && (
        <Alert tone="danger" title={`Another company holds ${d.parent_held_by_another_company}`}>
          {d.domain} sits under {d.parent_held_by_another_company}, which another company has verified, so only they can verify it.
          If {d.domain} is yours, ask whoever runs {d.parent_held_by_another_company} to add it to their account, or contact support@summitshield.io.
        </Alert>
      )}
      {d.held_by_another_company && !d.parent_held_by_another_company && !verified && (
        <Alert tone="danger">Another company has already verified this domain. A DNS record proves ownership over an email approval; if it&rsquo;s yours, add the DNS record below.</Alert>
      )}

      {!verified && <div className="grid-2">
        <Section title="Option 1: Approval by email" description="No technical changes. Needs two people at your company.">
          <ol className="check-list">
            <Check done={teamDone}>
              <strong>Someone on your team signs in with an @{d.domain} address.</strong>
              <span className="muted small block">
                {teamDone
                  ? `Done: ${d.proofs.team_member.map((m) => m.email).join(', ')}.`
                  : <>You sign in as {user.email}, which isn&rsquo;t on {d.domain}.{' '}
                    {org.can_manage
                      ? <><Link to="/company/team">Invite a colleague</Link> who has an @{d.domain} address; this step is done as soon as they join.</>
                      : <>Ask an owner or admin to invite a colleague with an @{d.domain} address from the Team page.</>}</>}
              </span>
            </Check>
            <Check done={approvalDone}>
              <strong>A colleague approves by email.</strong>
              <span className="muted small block">
                {approvalDone ? `Approved by ${d.proofs.approval[0].email}.` : 'Someone other than you, at an address on this domain.'}
              </span>
            </Check>
          </ol>
          {!verified && org.can_manage && (
            <div className="actions">
              <button type="button" className="btn btn-primary" onClick={() => setAsking(true)}>Ask a colleague to approve</button>
            </div>
          )}
          {d.requests.length > 0 && (
            <>
              <h3 className="label-small">Approval emails</h3>
              <div className="table-scroll">
                <table className="table table-stack">
                  <thead><tr><th>Sent to</th><th>Status</th><th>Sent</th></tr></thead>
                  <tbody>
                    {d.requests.map((r) => (
                      <tr key={r.id}>
                        <td data-label="Sent to" className="mono">{r.email}</td>
                        <td data-label="Status"><Pill tone={REQUEST_STATUS[r.status].tone}>{REQUEST_STATUS[r.status].label}</Pill></td>
                        <td data-label="Sent" className="small">{fmtDateTime(r.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Section>

        <Section title="Option 2: Add a DNS record" description="A DNS record is a setting for your domain, kept by whoever you bought the domain from or host it with (GoDaddy, Cloudflare, 123-reg…). Only someone who controls the domain can add one, so it proves ownership on its own. Send them the three lines below — it takes a couple of minutes.">
          <dl className="dns-record">
            <dt>Type</dt><dd><span className="mono">TXT</span> <span className="muted small">(a text note)</span></dd>
            <dt>Name / host</dt><dd><span className="mono">@</span> <span className="muted small">(means the domain itself: {d.dns.name})</span></dd>
            <dt>Value</dt><dd className="dns-value"><span className="mono">{d.dns.value}</span> <CopyButton text={d.dns.value} /></dd>
          </dl>
          {d.dns.last_checked_at && <p className="muted small">Last checked {fmtDateTime(d.dns.last_checked_at)}: {dnsResult(d.dns.last_result)}</p>}
          {discovery.dns_provider && (
            <p className="muted small">Your DNS is run by <strong>{discovery.dns_provider}</strong>.{discovery.dns_provider_help && <> <a href={discovery.dns_provider_help} target="_blank" rel="noreferrer">How to add a TXT record there</a>.</>}</p>
          )}
          <div className="actions">
            <button type="button" className="btn btn-secondary" onClick={checkDns} disabled={!!checking || !org.can_manage}>{checking === 'dns' ? 'Checking…' : 'Check now'}</button>
          </div>
        </Section>

        <Section title="Option 3: Add our code to your website" description="If you can paste code into your website (WordPress, Wix, Squarespace…), it proves the domain is yours on its own — and protects the site against copies.">
          <div className="actions">
            <Link className="btn btn-secondary" to="/company/website">Get the code</Link>
            <button type="button" className="btn btn-secondary" onClick={checkSite} disabled={!!checking || !org.can_manage}>{checking === 'site' ? 'Checking…' : 'Check my site'}</button>
          </div>
          {d.website_check_result && <p className="muted small">Last checked: {/^found/i.test(d.website_check_result) ? 'our code is on your site.' : `not found yet (${d.website_check_result}).`}</p>}
        </Section>
      </div>}

      <EmailSecurity domain={d.domain} security={discovery.email_security} help={discovery.dns_provider_help} provider={discovery.dns_provider} />

      <DiscoverySections key={d.id} discovery={discovery} base={base} canManage={org.can_manage} />

      {asking && (
        <AskColleagueModal base={base} domain={d} onClose={() => setAsking(false)}
          onSent={(res) => { setAsking(false); setData({ ...data, domain: res.domain }); setNotice({ tone: 'success', text: `Email sent to ${res.email}. The link works for 24 hours.` }); }} />
      )}
    </div>
  );
}
